import React, { useEffect, useState } from "react";
import {
  Card,
  CardContent,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  SelectChangeEvent,
  Button,
  TextField,
  Tooltip,
  IconButton,
} from "@mui/material";
import { RemoveRedEye } from "@mui/icons-material";

import { AuditLogData } from "../../types/AuditLog";
import { initialParamData, ParamData } from "../../types/Param";
import { Box } from "@mui/system";
import { formatDateTime } from "../../utils/DateHelper";
import AuditDetail from "./AuditDetail";

export interface UserLogProps {
  users: string[];
  activities: AuditLogData[];
  paramData: ParamData;
  setParamData: React.Dispatch<React.SetStateAction<ParamData>>;
  onSubmit: () => void;
}

const UserLog: React.FC<UserLogProps> = ({
  users,
  activities,
  paramData,
  setParamData,
  onSubmit,
}) => {
  const [expandedRowId, setExpandedRowId] = useState<number | null>(null);

  const handleChange = (
    e:
      | React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
      | SelectChangeEvent
  ) => {
    const { name, value } = e.target;

    setParamData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleViewClick = async (data: AuditLogData) => {
    setExpandedRowId((prev) => (prev === data.id ? null : data.id));
  };

  const handleOnSubmit = async () => {
    if (onSubmit) {
      await onSubmit();
    }
  };

  useEffect(() => {
    if (paramData.user && paramData.startingFrom && paramData.endingOn) {
      handleOnSubmit();
    }
  }, [paramData.user, paramData.startingFrom, paramData.endingOn]);

  return (
    <Card sx={{ mb: 4, p: 2 }}>
      <CardContent>
        <Typography variant="h6" fontWeight="600" mb={2}>
          SYSTEM USERS AND THEIR ACTIVITIES
        </Typography>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            flexWrap: "wrap",
            gap: 2,
            mb: 3,
          }}
        >
          {/* Select Entity */}

          {/* Starting From Date */}
          <TextField
            name="startingFrom"
            label="Starting From"
            type="date"
            size="small"
            value={paramData.startingFrom || ""}
            onChange={handleChange}
            InputLabelProps={{ shrink: true }}
            inputProps={{
              max: new Date().toISOString().split("T")[0], // 🔒 max = today
            }}
            sx={{ minWidth: 170 }}
          />

          {/* Ending On Date */}
          <TextField
            name="endingOn"
            label="Ending On"
            type="date"
            size="small"
            value={paramData.endingOn || ""}
            onChange={handleChange}
            InputLabelProps={{ shrink: true }}
            inputProps={{
              max: new Date().toISOString().split("T")[0], // 🔒 max = today
            }}
            sx={{ minWidth: 170 }}
          />
          <FormControl size="small" sx={{ minWidth: 220 }}>
            <InputLabel>Select user</InputLabel>
            <Select
              name="user"
              label="Select Entity"
              onChange={handleChange}
              value={paramData.user || ""}
            >
              <MenuItem value="">
                <em>-- Select User --</em>
              </MenuItem>

              {users.map((e) => (
                <MenuItem key={e} value={e}>
                  {e}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          {/* Find Button */}
        </Box>

        {/* Activities Table */}
        <TableContainer component={Paper} sx={{ maxHeight: 450 }}>
          <Table size="small" stickyHeader>
            <TableHead sx={{ backgroundColor: "#f5f5f5" }}>
              <TableRow>
                <TableCell>ENTITY</TableCell>
                <TableCell>USER ACTION</TableCell>
                <TableCell>DATE</TableCell>
                <TableCell>ACTION</TableCell>
              </TableRow>
            </TableHead>

            <TableBody>
              {activities.map((act) => (
                <React.Fragment key={act.id}>
                  <TableRow>
                    <TableCell>{act.entityName}</TableCell>
                    <TableCell>{act.userAction}</TableCell>
                    <TableCell>{formatDateTime(act.createdAt)}</TableCell>
                    <TableCell>
                      <Tooltip title="View Details">
                        <IconButton
                          color="primary"
                          onClick={() => handleViewClick(act)}
                          size="small"
                        >
                          <RemoveRedEye />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>

                  {expandedRowId === act.id && (
                    <TableRow>
                      <TableCell
                        colSpan={4}
                        sx={{ backgroundColor: "#f9f9f9" }}
                      >
                        <Box p={2}>
                          <AuditDetail details={act.auditLogDetails} />
                        </Box>
                      </TableCell>
                    </TableRow>
                  )}
                </React.Fragment>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </CardContent>
    </Card>
  );
};

export default UserLog;
